/**
 * Turning an API failure into a sentence a user can act on.
 *
 * The data provider rejects with ra-core's HttpError, which carries three
 * things: `status`, `body` (the parsed JSON, or null when the response was not
 * JSON at all) and `message` (`body.message` if there was one, otherwise the
 * bare HTTP status text). Only the body is worth reading; everything else needs
 * a sentence of our own.
 */

/** DRF keys that carry the explanation, read in this order before anything else */
const PRIORITY_KEYS = ['data', 'detail', 'non_field_errors', 'message', 'error'];

/** long enough for any backend sentence, short enough for a toast */
const MAX_MESSAGE_LENGTH = 300;

/** an opening tag: a server error page, not a message to put in front of anyone */
const LOOKS_LIKE_MARKUP = /<[a-z!/]/i;

/**
 * What ra-core cannot tell us. `fetchJson` throws a non-JSON body away, so a
 * proxy's 413 arrives as `{status: 413, body: null}` with nothing but the HTTP
 * status text — which is "" over HTTP/2 and explains nothing over HTTP/1.
 */
const STATUS_MESSAGES: { [status: number]: string } = {
    413: 'The request was too large for the server to accept.',
    502: 'The server could not be reached. Please try again.',
    503: 'The server is unavailable right now. Please try again.',
    504: 'The server took too long to respond. Please try again.',
};

/**
 * One level of a DRF error value, flattened to strings.
 *
 * Deliberately not recursive: useServerErrors maps each body key onto one form
 * field, and a nested object has to stay a single string for that to work.
 */
export const toMessages = (value: any): string[] =>
    (Array.isArray(value) ? value : [value]).map((v) => (typeof v === 'string' ? v : JSON.stringify(v)));

/** the body's values, the keys that explain things first */
const orderedValues = (body: any): any[] => {
    const keys = Object.keys(body);
    const known = PRIORITY_KEYS.filter((key) => keys.includes(key));
    const rest = keys.filter((key) => !PRIORITY_KEYS.includes(key));
    return [...known, ...rest].map((key) => body[key]);
};

/** every string anywhere in the body — a field error can be nested arbitrarily */
const flatten = (value: any): string[] => {
    if (typeof value === 'string') return [value];
    if (Array.isArray(value)) return value.flatMap(flatten);
    if (value && typeof value === 'object') return orderedValues(value).flatMap(flatten);
    if (value === null || value === undefined) return [];
    return [JSON.stringify(value)];
};

/**
 * `notify` hands its argument to node-polyglot — no i18nProvider is configured,
 * so every string goes through transformPhrase, which interpolates `%{...}`,
 * splits on `||||` and throws on a non-string. None of that is what a server
 * sentence means, so neutralise it before the message ever gets there.
 */
const sanitize = (text: string): string => {
    const collapsed = text.replace(/\s+/g, ' ').trim().replace(/%\{/g, '%(').replace(/\|\|\|\|/g, '|');
    return collapsed.length > MAX_MESSAGE_LENGTH ? collapsed.slice(0, MAX_MESSAGE_LENGTH - 1) + '…' : collapsed;
};

/**
 * The most useful thing we can say about a failed request, always as a
 * non-empty string.
 *
 * @param error   whatever the data provider rejected with
 * @param fallback what to say when the error explains nothing
 */
export const getServerErrorMessage = (error: any, fallback = 'Something went wrong'): string => {
    const body = error?.body;

    if (typeof body === 'string') {
        // only fetchNonJson gets here, and what it hands back may be an error
        // page — markup belongs in the network tab, not in a toast
        const text = sanitize(body);
        if (text && !LOOKS_LIKE_MARKUP.test(text)) return text;
    } else if (body && typeof body === 'object') {
        const text = sanitize(flatten(body).filter((message) => message.trim()).join(' '));
        if (text && !LOOKS_LIKE_MARKUP.test(text)) return text;
    }

    const byStatus = STATUS_MESSAGES[error?.status];
    if (byStatus) return byStatus;

    // With a status, `message` is only the HTTP status text and says no more
    // than the fallback does. Without one this was never a response at all —
    // a network failure, where the thrown message is the only thing there is.
    const message = typeof error?.message === 'string' ? sanitize(error.message) : '';
    if (message && !error?.status) return message;

    return fallback;
};
